import { useTranslation } from 'react-i18next';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { availabilityTypeConstant, jobTypeConstant, bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { JobPostCard } from 'us-react-components/dist/JobPostCard/JobPostCard';
import { DateFormat, formatDateFromString } from 'us-react-components/dist/utils/date';
import { Job } from 'us-web-services';
import { Routes } from '../../../../../Routes';
import { useDependencies } from '../../../../../dependencies';
import { Button, ButtonType } from '../../../../Button';

type Props = {
  jobs: Job[];
  openJobCount: number;
}

export const DashboardJobPosts = (props: Props): JSX.Element => {
  const { stores } = useDependencies();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { jobs = [], openJobCount } = props;
  const { user } = stores.user;

  const handleJobInterest = (id: number): void => {
    navigate(generatePath(Routes.job.interest, { id }));
  };

  const handleJobEdit = (id: number): void => {
    navigate(generatePath(Routes.job.edit, { id }));
  };

  const handleJobsView = (): void => {
    navigate(generatePath(Routes.parent.posts));
  };

  const handleSearch = (job: Job): void => {
    const oneTimeAvailability = job.type === jobTypeConstant.ONE_TIME ? 'Occasional' : 'All';
    const availability = job.availability ? job.availability : oneTimeAvailability;
    navigate(
      {
        pathname: generatePath(Routes.parent.sitters),
        search: `${createSearchParams({
          userId: (user.id || 0).toString(),
          parentId: (user.id || 0).toString(),
          start: job.booking?.start ?? '',
          end: job.booking?.end ?? '',
          availabilityType: availabilityTypeConstant.find((a: { type?: string; }) => a.type === availability)?.type ?? 'All',
          searchTypeCode: availabilityTypeConstant.find((a: { type?: string; }) => a.type === availability)?.code ?? 'ALL',
          verticalTypeId: (job.booking?.typeId ?? bookingVerticalType.CHILD_CARE).toString(),
          zip: job.booking?.address?.zip ?? '',
          city: job.booking?.address?.city ?? '',
          state: job.booking?.address?.state ?? '',
        })}`,
      });
  }

  const formatDate = (bookingDatetime: string): string => formatDateFromString(bookingDatetime, DateFormat.DATE_WEEKDAY_FORMAT);
  const formatTime = (bookingDatetime: string): string => formatDateFromString(bookingDatetime, DateFormat.TIME_FORMAT);

  return (
    <div className='p-4 place-content-center rounded-2xl border-neutral-100 border-2 group w-full h-full'>
      <div className='grid grid-cols-1 gap-2'>
        {jobs.map((job, index) => (
          <JobPostCard
            key={job.id}
            classes={index < jobs.length - 1 || openJobCount > jobs.length ? 'border-b-2 pb-2' : undefined}
            job={job}
            formatDate={formatDate}
            formatTime={formatTime}
            handleSearchClick={handleSearch}
            handleEditClick={handleJobEdit}
            handleInterestClick={handleJobInterest} />
        ))}
      </div>
      {openJobCount > 2 && (
        <div className='inline-block mt-6 mb-2'>
          <Button
            buttonType={ButtonType.secondary}
            className='text-sm'
            onClick={handleJobsView}>{t('dashboard.widgets.myJobPosts.viewAll')}
            <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
          </Button>
        </div>
      )}
    </div>
  );
};
